import { computed, ref, type Ref } from 'vue';
import { useModal } from 'vue-final-modal';
import { useExcelBlobExport, type Pagination, type SortOrder } from '@/shared/lib';

import { exportRecordListExcel } from '../api/record-list-export.api';
import type {
  RecordListFilters,
  RecordSearchType,
  RecordSortField,
} from '../lib/record-list-query';
import RecordListExportModal from '../ui/RecordListExportModal.vue';

export function useRecordListExport(options: {
  searchTerm: Ref<string>;
  searchFieldId: Ref<string>;
  sort: Ref<string>;
  order: Ref<string>;
  filters: Ref<RecordListFilters>;
  metadata: Ref<Pagination | null | undefined>;
}) {
  const includePreviousRevisions = ref(false);

  const { handleExportToExcel: runExport, isExporting } = useExcelBlobExport(
    () =>
      exportRecordListExcel({
        term: options.searchTerm.value,
        searchType: options.searchFieldId.value as RecordSearchType,
        sort: (options.sort.value || undefined) as RecordSortField | undefined,
        order: (options.order.value || undefined) as SortOrder | undefined,
        status: options.filters.value.status,
        generatedRecords: options.filters.value.generatedRecords,
        includePreviousRevisions: includePreviousRevisions.value,
      }),
    { singular: 'revisão', plural: 'revisões', gender: 'female' }
  );

  const isExportDisabled = computed(() => {
    if (isExporting.value) return true;

    const total = options.metadata.value?.total;
    return total !== undefined && total === 0;
  });

  const scopeModal = useModal({
    component: RecordListExportModal,
    attrs: {
      // A getter, so the modal sees the flag change without patchOptions.
      get isExporting() {
        return isExporting.value;
      },
      onClose: () => scopeModal.close(),
      // The modal stays open, and sealed, until the file is ready: AC10.
      onConfirm: async (includePrevious: boolean) => {
        includePreviousRevisions.value = includePrevious;

        try {
          await runExport();
        } finally {
          await scopeModal.close();
        }
      },
    },
  });

  const handleExportToExcel = () => {
    if (isExporting.value) return;

    scopeModal.open();
  };

  return { handleExportToExcel, isExporting, isExportDisabled };
}
